import { Link } from "react-router-dom";
import { SkipBack, SkipForward, Volume2, VolumeX } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { usePlayer } from "@/context/PlayerContext";
import { formatTime } from "@/lib/format";
import { NwSlider } from "./NwSlider";
import { Cover, PlayPauseIcon } from "./PlayerBits";
import { PLAYER } from "@/constants/testIds";

export const FullPlayer = ({ open, onOpenChange }) => {
  const { track, playing, loading, time, volume, toggle, seek, setVolume, next, prev } = usePlayer();
  if (!track) return null;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" data-testid={PLAYER.full} className="bg-[#07080C] border-white/10 text-slate-100 rounded-t-3xl max-h-[95vh] overflow-y-auto">
        <div className="mx-auto max-w-md space-y-6 pb-[env(safe-area-inset-bottom)]">
          <div className="mx-auto h-1 w-10 rounded-full bg-white/20" />
          <Cover track={track} className="mx-auto aspect-square w-full max-w-[320px] rounded-2xl shadow-[0_20px_60px_rgba(255,30,39,0.25)]" />
          <div className="text-center space-y-1">
            <SheetTitle className="font-display text-2xl font-bold text-white">{track.title}</SheetTitle>
            <SheetDescription className="text-slate-400">{track.artist_name}</SheetDescription>
            <Link to={`/track/${track.id}`} onClick={() => onOpenChange(false)} data-testid={PLAYER.trackLink} className="inline-block pt-1 font-mono text-[11px] uppercase tracking-[0.25em] text-nw-red hover:underline">Likes & comments</Link>
          </div>
          <div>
            <NwSlider value={time.current} max={time.duration} onChange={seek} testId={PLAYER.seek} label="Seek" />
            <div className="flex justify-between font-mono text-[11px] text-slate-500">
              <span data-testid={PLAYER.current}>{formatTime(time.current)}</span><span data-testid={PLAYER.duration}>{formatTime(time.duration)}</span>
            </div>
          </div>
          <div className="flex items-center justify-center gap-8">
            <button data-testid={PLAYER.prev} onClick={prev} aria-label="Previous" className="text-slate-300 hover:text-white transition-colors"><SkipBack className="h-6 w-6" fill="currentColor" /></button>
            <button data-testid={PLAYER.fullToggle} onClick={toggle} aria-label={playing ? "Pause" : "Play"}
              className="grid h-16 w-16 place-items-center rounded-full bg-nw-red text-white shadow-[0_0_25px_rgba(255,30,39,0.6)] ring-2 ring-slate-300/30 transition-transform hover:scale-105 active:scale-95">
              <PlayPauseIcon playing={playing} loading={loading} className="h-7 w-7" />
            </button>
            <button data-testid={PLAYER.next} onClick={next} aria-label="Next" className="text-slate-300 hover:text-white transition-colors"><SkipForward className="h-6 w-6" fill="currentColor" /></button>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid={PLAYER.mute} onClick={() => setVolume(volume > 0 ? 0 : 0.9)} aria-label="Mute" className="text-slate-400 hover:text-white">
              {volume > 0 ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
            </button>
            <NwSlider value={volume} max={1} onChange={setVolume} testId={PLAYER.volume} label="Volume" />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
